import { Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TableColumn, TableColumnType } from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';
import { BehaviorSubject, Subscription } from 'rxjs';

export interface TableEvent<T = TableEntity> {
  index: number;
  tableData: T;
}

export interface TableEntity {
  _id: string;
  [key: string]: AnyType;
}

interface FilterObject {
  label: string;
  placeholder: string;
  name: string;
  input_type: string;
  multiple?: boolean;
  options: { id: string; name: string }[];
  onSelectionChange?: (event: AnyType) => AnyType;
  defaultValue: AnyType;
}

@Component({
  selector: 'sp-custom-mat-table',
  templateUrl: './custom-mat-table.component.html',
  styleUrls: ['./custom-mat-table.component.scss'],
})
export class CustomMatTableComponent implements OnInit, OnDestroy {
  @ContentChild('tagGroupsEdit', { read: TemplateRef }) tagGroupsEdit!: TemplateRef<AnyType>;
  // table columns defined as per TableColumn model
  private _tableCols: TableColumn[] = [];
  @Input() set tableCols(tableCols: TableColumn[]) {
    this._tableCols = tableCols;
    this.displayedColumns = this._tableCols.map((c) => c.columnDef);
  }

  get tableCols() {
    return this._tableCols;
  }

  // columns to display in table
  displayedColumns: string[] = [];

  // Subject that emits data from parent component
  @Input() tableData = new BehaviorSubject<AnyType>('');

  // if action column required
  @Input() actionColumn!: boolean;

  // if edit action to be shown
  @Input() showEdit!: boolean | (<T>(data: T) => boolean);

  // if delete action to be shown
  @Input() showDelete!: boolean | (<T>(data: T) => boolean);

  // if download action to be shown
  @Input() showDownload!: boolean | (<T>(data: T) => boolean);

  // if duplicate action to be shown
  @Input() showDuplicate!: boolean | (<T>(data: T) => boolean);

  // if multiselect required
  @Input() multiSelect!: boolean;

  // To show the search filter
  @Input() showSearchFilter!: boolean;

  // To show the mat-paginator
  @Input() showMatPaginator = true;

  // To add row click
  @Input() addRowClick!: boolean;

  // Template ref for filter menu
  @Input() template!: TemplateRef<AnyType>;

  // To show the filter menu
  @Input() showFilterMenu!: boolean;

  // Filter objects array
  @Input() filterObjects: FilterObject[] = [];

  // Table name
  @Input() tableName?: string = '';

  // Disable mat sort
  @Input() matSortDisabled = false;

  // Table default column sort
  @Input() defaultColumnSort?: MatSortable;

  /**
   * Disables the download asset button if true
   */
  @Input() disableDownload = false;

  // send event from table component to its parent component
  @Output() clickEdit = new EventEmitter<AnyType>();
  @Output() clickDownload = new EventEmitter<AnyType>();
  @Output() clickDuplicate = new EventEmitter<AnyType>();
  @Output() clickDelete = new EventEmitter<TableEvent<TableEntity>>();
  @Output() selectionChange = new EventEmitter<AnyType>();
  @Output() applyFilterMenu = new EventEmitter<AnyType>();
  @Output() rowClick = new EventEmitter<AnyType>();

  @Output() masterToggleClick = new EventEmitter<AnyType>();
  @Output() checkboxToggleClick = new EventEmitter<AnyType>();

  /* Data source for table */
  dataSource!: MatTableDataSource<AnyType>;

  /* Table data subscription for unsubscribing */
  dataSubs!: Subscription;

  isAllSelected = false;

  searchText!: string;
  searchPlaceholder = 'Search...';

  TableColumnType = TableColumnType;

  tagPlaceHolderText = $localize`Search tags...`;

  // for sort and pagination
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  ngOnInit() {
    /* Subscribing to table data and updating the datasource */
    this.dataSubs = this.tableData.subscribe((data: AnyType) => {
      this.dataSource = new MatTableDataSource(data);
      if (data.length && this.multiSelect) this.checkIfAllSelected(data);
      this.dataSource.filter = this.searchText;
      this.dataSource.paginator = this.paginator;
      if (this.defaultColumnSort) {
        this.sort.sort(this.defaultColumnSort);
        this.sort.sortChange.emit();
      }
      this.dataSource.sort = this.sort;
    });

    // adding action column if actionColumn is true
    if (this.actionColumn) {
      this.tableCols.push({
        columnDef: 'actions',
        header: $localize`:table header:Actions`,
      });
    }

    this.displayedColumns = this.tableCols.map((c) => c.columnDef);

    // adding multiselect column if multiselect is true
    if (this.multiSelect) {
      this.displayedColumns.unshift('select');
    }
  }

  ngOnDestroy(): void {
    if (this.dataSubs) this.dataSubs.unsubscribe();
  }

  canEdit(tableData: AnyType) {
    if (this.showEdit instanceof Function) {
      return this.showEdit(tableData);
    }
    return this.showEdit;
  }

  // edit action
  editDialog(tableData: AnyType) {
    this.clickEdit.emit(tableData);
  }

  canDelete(tableData: AnyType) {
    if (this.showDelete instanceof Function) {
      this.showDelete(tableData);
    }
    return this.showDelete;
  }

  canDownload(tableData: AnyType) {
    if (this.showDownload instanceof Function) {
      this.showDownload(tableData);
    }
    return this.showDownload;
  }

  canDuplicate(tableData: AnyType) {
    if (this.showDuplicate instanceof Function) {
      this.showDuplicate(tableData);
    }
    return this.showDuplicate;
  }

  // download action
  downloadDialog(index: number, tableData: AnyType) {
    this.clickDownload.emit({ index, tableData });
  }

  // delete action
  deleteDialog(index: number, tableData: AnyType) {
    this.clickDelete.emit({ index, tableData });
  }

  duplicateDialog(index: number, tableData: AnyType) {
    this.clickDuplicate.emit({ index, tableData });
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.masterToggleClick.emit();
  }

  /* Checks if all objects of table data have checked = true and sets isAllSelected */
  checkIfAllSelected(data: AnyType) {
    const allSelected = data.filter((obj: AnyType) => obj.checked);
    if (allSelected.length === this.dataSource.data.length) {
      this.isAllSelected = true;
    } else {
      this.isAllSelected = false;
    }
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: AnyType): string {
    if (!row) {
      return $localize`:label row selection:${this.isAllSelected ? 'deselect' : 'select'} all`;
    }
    return $localize`:label row selection:${row.checked ? 'deselect' : 'select'} row ${row.position + 1}`;
  }

  /* On checkbox click */
  checkBoxToggle(row: AnyType): void {
    this.checkboxToggleClick.emit(row);
  }

  /* To check if AnyType object if tabledata has checked = true, for mastertoggle indeterminate */
  hasValue() {
    return this.dataSource.data.find((obj: AnyType) => obj.checked);
  }

  /* Filtering the data */
  doFilter = (value: string) => {
    this.dataSource.filter = value.trim().toLocaleLowerCase();
  };

  /* Filter menu apply button click event */
  applyFilter(event: AnyType) {
    this.applyFilterMenu.emit(event);
  }

  /* On mat table row click */
  onRowClick(row: AnyType) {
    this.rowClick.emit(row);
  }

  convertHtmlToText(value: any) {
    return value.replace(/<.*?>/g, '');
  }

  /**
   * SearchField methods
   */

  searchTextChanged(event: string) {
    this.searchText = event;
    this.doFilter(event);
  }

  clearSearch(event: AnyType) {
    this.searchText = event;
    this.doFilter(event);
  }
}
